<template>
  <transition
    name="alert-notification"
    appear
    @before-leave="onBeforeLeave"
    @after-leave="$emit('close')"
  >
    <div
      v-if="show"
      class="alert-notification"
      :class="[
        `is-${type}`,
        { 'with-action': hasAction },
        { 'is-notification': !inline },
        { 'is-inline': inline }
      ]"
      role="alert"
    >
      <div class="alert-notification__icon">
        <i
          class="fas"
          :class="[
            type === 'success' ? 'fa-check-circle' : '',
            type === 'info' ? 'fa-info-circle' : '',
            type === 'warning' ? 'fa-exclamation-triangle' : '',
            type === 'error' ? 'fa-exclamation-circle' : ''
          ]"
        />
      </div>
      <div class="alert-notification__content">
        <div
          v-if="title"
          class="alert-notification__title"
        >
          {{ title }}
        </div>
        <div class="alert-notification__message">
          <slot>{{ message }}</slot>
        </div>
      </div>
      <div
        v-if="hasAction"
        class="alert-notification__action"
      >
        <slot name="action" />
      </div>
      <button
        v-if="dismissible"
        class="alert-notification__close"
        aria-label="close"
        @click="closeNotification"
      >
        <span class="icon"><i class="fas fa-times" /></span>
      </button>
      <div
        v-if="autoClose"
        class="alert-notification__progress"
      >
        <div
          class="alert-notification__progress-bar"
          :style="{ animationDuration: `${duration}ms` }"
        />
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AlertNotification',
  props: {
    type: {
      type: String,
      default: 'info',
      validator: value => ['info', 'success', 'warning', 'error'].includes(value),
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    dismissible: {
      type: Boolean,
      default: true,
    },
    autoClose: {
      type: Boolean,
      default: false,
    },
    duration: {
      type: Number,
      default: 5000,
    },
    hasAction: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      show: true,
      timeoutId: null,
    };
  },
  mounted() {
    if (this.autoClose) {
      this.setAutoCloseTimeout();
    }
  },
  beforeUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  },
  methods: {
    closeNotification() {
      this.show = false;
    },
    setAutoCloseTimeout() {
      this.timeoutId = setTimeout(() => {
        this.closeNotification();
      }, this.duration);
    },
    onBeforeLeave(el) {
      el.style.height = `${el.offsetHeight}px`;
      el.style.marginTop = '0';
      el.style.marginBottom = '0';
      setTimeout(() => {
        el.style.height = '0';
        el.style.marginTop = '0';
        el.style.marginBottom = '0';
      }, 10);
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/abstracts/colors';
@import '@/styles/abstracts/animations';

$alert-notification-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
$alert-notification-border-radius: 12px;
$alert-notification-icon-size: 24px;

// Alert notification animation transitions
.alert-notification-enter-active,
.alert-notification-leave-active {
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55);
  overflow: hidden;
}

.alert-notification-enter-from {
  transform: translateY(-20px) scale(0.98);
  opacity: 0;
}

.alert-notification-leave-to {
  transform: translateX(30px);
  opacity: 0;
  height: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.alert-notification {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: $alert-notification-border-radius;
  box-shadow: $alert-notification-shadow;
  padding: 16px;
  margin: 16px 0;
  border-left: 5px solid currentColor;
  transition: $transition-base;
  box-sizing: border-box; /* Include padding in width calculation */

  /* Notification style (positioned absolutely) */
  &.is-notification {
    position: absolute;
    right: 0;
    top: 10px;
    z-index: 1000;
  }

  /* Inline style (stays in document flow) */
  &.is-inline {
    display: flex !important;
    position: relative;
    width: 100%;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }

  // Color variants
  &.is-success {
    color: $success;
    background-color: lighten($success, 48%);
  }

  &.is-info {
    color: $info-dark;
    background-color: lighten($info, 46%);
  }

  &.is-warning {
    color: $warning;
    background-color: lighten($warning, 45%);
  }

  &.is-error {
    color: $danger;
    background-color: lighten($danger, 40%);
  }

  // Mobile responsive adjustments
  @media screen and (max-width: 768px) {
    &__title {
      white-space: normal !important;
    }

    &__message {
      white-space: normal !important;
    }
  }

  // Dark mode support
  .body--dark & {
    background-color: #2a2a35;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);

    &.is-success {
      color: lighten($success, 10%);
      background-color: darken-color($success, 40%);
    }

    &.is-info {
      color: lighten($info, 10%);
      background-color: darken-color($info, 40%);
    }

    &.is-warning {
      color: lighten($warning, 5%);
      background-color: darken-color($warning, 40%);
    }

    &.is-error {
      color: lighten($danger, 10%);
      background-color: darken-color($danger, 40%);
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: $alert-notification-icon-size;
    height: $alert-notification-icon-size;
    font-size: $alert-notification-icon-size;
    margin-right: 16px;
    flex-shrink: 0;
    min-width: $alert-notification-icon-size; /* Prevent icon from shrinking */
  }

  &__content {
    padding-right: 12px;
    overflow: visible; /* Allow content to be visible */
  }

  &__title {
    font-weight: 600;
    margin-bottom: 4px;
    font-size: 1rem;
    white-space: nowrap; /* No wrapping */
    overflow: visible; /* Allow text to extend past container */
  }

  /* Apply different title treatment for inline alerts */
  &.is-inline &__title {
    white-space: normal; /* Allow title to wrap for inline alerts */
    overflow: hidden; /* Hide overflow for inline alerts */
  }

  &__message {
    font-size: 0.94rem;
    line-height: 1.4;
    color: inherit;
    opacity: 0.9;
    white-space: nowrap; /* No wrapping */
    overflow: visible; /* Allow text to extend past container */
  }

  /* Apply different text treatment for inline alerts */
  &.is-inline &__message {
    white-space: normal; /* Allow text to wrap for inline alerts */
    overflow: hidden; /* Hide overflow for inline alerts */
  }

  &__action {
    margin-left: 16px;
    display: flex;
    align-items: center;
    flex-shrink: 0; /* Prevent shrinking */
  }

  &__close {
    background: transparent;
    border: none;
    color: inherit;
    opacity: 0.6;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
    transition: $transition-fast;
    margin-left: 12px;
    flex-shrink: 0;
    min-width: 14px; /* Prevent shrinking */

    &:hover {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  &__progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  &__progress-bar {
    height: 100%;
    width: 100%;
    background-color: currentColor;
    animation: progress-shrink linear forwards;
    transform-origin: left;
  }

  @keyframes progress-shrink {
    0% {
      transform: scaleX(1);
    }
    100% {
      transform: scaleX(0);
    }
  }

  // Glass morphism effect for a fancy look
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    border-radius: $alert-notification-border-radius 0 0 0;
    pointer-events: none;
  }

  // Additional animation for icon
  &__icon i {
    animation: alert-icon-pop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
  }

  @keyframes alert-icon-pop {
    0% {
      transform: scale(0.8);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
}
</style>